import { useEffect, useState } from 'react';
import { FullPageLoader } from 'components/FullPageLoader';
import { tokenService } from 'views/services/tokenService';
import { TNetworkList } from 'views/types/tokens';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import cn from 'clsx';
import { TokenDetailsDialog } from './TokenDetailsDialog';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';

const columnHelper = createColumnHelper<TNetworkList>();

const columns = [
  columnHelper.accessor('coin', {
    header: 'Token',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('netWork', {
    header: 'Network',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('depositEnable', {
    header: 'Deposit',
    cell: info => (
      <span
        className={cn('px-2 py-1 rounded font-medium border', {
          'text-green-600 border-green-600': info.getValue(),
          'text-red-600 border-red-600': !info.getValue(),
        })}
      >
        {info.getValue() ? 'Enable' : 'Disable'}
      </span>
    ),
  }),
  columnHelper.accessor('withdrawEnable', {
    header: 'Withdraw',
    cell: info => (
      <span
        className={cn('px-2 py-1 rounded font-medium border', {
          'text-green-600 border-green-600': info.getValue(),
          'text-red-600 border-red-600': !info.getValue(),
        })}
      >
        {info.getValue() ? 'Enable' : 'Disable'}
      </span>
    ),
  }),
];

export const TokenInfo = () => {
  const [loading, setLoading] = useState(true);
  const [tokenStatuses, setTokenStatuses] = useState<TNetworkList[]>([]);
  const [selectedToken, setSelectedToken] = useState<TNetworkList | null>(null);
  const { canUpdate } = usePermissions();

  const fetchData = async () => {
    setLoading(true);
    const tokenInfo = await tokenService.getTokenInfo();
    if (tokenInfo) {
      setTokenStatuses(tokenInfo);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedToken && !canUpdate(PERMISSION_RESOURCES.TOKENS_INFO)) {
      setSelectedToken(null);
    }
  }, [canUpdate, selectedToken]);

  const table = useReactTable({
    data: tokenStatuses,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const onCloseInfo = () => {
    setSelectedToken(null);
  };

  if (loading) return <FullPageLoader />;

  return (
    <>
      <TokenDetailsDialog
        open={!!selectedToken}
        onClose={onCloseInfo}
        tokenData={selectedToken}
      />

      <div className="p-4">
        <table className="min-w-full border-collapse border border-gray-300">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="dark:bg-gray-800 dark:text-white border border-gray-300 px-4 py-2 text-left bg-gray-100"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                className={`dark:hover:bg-gray-800 hover:bg-gray-50 ${
                  canUpdate(PERMISSION_RESOURCES.TOKENS_INFO)
                    ? 'hover:bg-gray-50 dark:hover:bg-[black]/20 cursor-pointer'
                    : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (canUpdate(PERMISSION_RESOURCES.TOKENS_INFO)) {
                    setSelectedToken(row.original);
                  }
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className="dark:text-white border border-gray-300 px-4 py-2"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
